import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContainerMovementListItem } from './model/ContainerMovementListItem';
import { ContainerStatusConstant } from './constant/ContainerStatusConstant';
import StatusFlags from './enum/StatusFlags';
import ContainerUpdateStatusRequest from './model/ContainerUpdateStatusRequest';
import ContainerStatus from './model/ContainerStatus';
import { catchError, Observable, map, of, take } from 'rxjs';
import { ContainerCompleteLegRequest } from './model/ContainerCompleteLegRequest';
import { ContainerStatusColumnConfig } from './constant/ContainerStatusColumnConfig';
import { ContainerDisplayStatus } from './model/ContainerDisplayStatus';
import { PortStatus } from './model/PortStatus';
import { HazardCode } from './model/HazardCode';
import { LoadUnloadType } from './model/LoadUnloadType';
import ContainerType from './model/ContainerType';
import ContainerSize from './model/ContainerSize';
import { Division } from '../divisons/models/Division';
import { JobTypeEnum } from './enum/JobTypeEnum';
import { JobType } from './model/JobType';
import { ContainerJobDefault } from './model/ContainerJobDefault';
import { CreateContainerJobRequest } from './model/CreateContainerJobRequest';
import { Port } from './model/depots/Port';
import { VesselVisit } from './model/VesselVisit';
import { ContainerEyeViewData } from './model/ContainerEyeViewData';
import ContainerUpdateRequest from './model/ContainerUpdateRequest';
import ContainerUpdateListItem from './model/ContainerUpdateListItem';
import { SearchDateOption } from './enum/SearchDateOption';

@Injectable({
  providedIn: 'root'
})
export class ContainerService {

  constructor(private http$: HttpClient) { }

  getContainerJobs(from: Date | null, to: Date | null, searchDateOption: SearchDateOption) {
    const options = {
      params: {
        searchDateOption,
        from: from?.toUTCString() || '',
        to: to?.toUTCString() || ''
      }
    }
    return this.http$.get<ContainerMovementListItem[]>(`/api/container/deliverystatus/items`, options);
  }

  getContainerEyeViewData(containerJobItemId: number): Observable<ContainerEyeViewData> {
    return this.http$.get<ContainerEyeViewData>(`/api/container/containerJobItem/${containerJobItemId}/eyeview`).pipe(take(1));
  }

  createContainerJob(request: CreateContainerJobRequest): Observable<HttpResponse<any>> {
    return this.http$.post<any>(`/api/container`, request, { observe: 'response' })
      .pipe(take(1));
  }

  getContainerJobLegs(from: Date | null, to: Date | null, searchDateOption: SearchDateOption) {
    const options = {
      params: {
        searchDateOption,
        from: from?.toUTCString() || '',
        to: to?.toUTCString() || ''
      }
    }
    return this.http$.get<ContainerMovementListItem[]>(`/api/container/deliverystatus/legs`, options);
  }

  getContainerStatusList() {
    return this.http$.get<ContainerStatus[]>("/api/container/jobStatuses").pipe(
      map(result => {
        return result;
      })
    );
  }

  patchContainerJobItem({ containerJobItemId, fieldName, value }: { containerJobItemId: number, fieldName: string, value: any }) {
    return this.http$.patch(`/api/container/containerJobItem/${containerJobItemId}`, { columnName: fieldName, columnValue: value });
  }

  getScheduledVessels(portCode: string): Observable<VesselVisit[]> {
    return this.http$.get<VesselVisit[]>(`api/container/scheduledVessels/${portCode}`)
      .pipe(take(1));
  }

  getContainerPorts(): Observable<Port[]> {
    return this.http$.get<any[]>(`/api/container/ports`)
      .pipe(
        map(results => {
          const obj = results
            .map(data => {
              data.display = data.name;
              if (data.code && data.code.length > 0) {
                data.display = `${data.display} (${data.code})`;
              }
              return data;
            });
          return obj;
        })
      ).pipe(take(1));
  }

  getContainerParksByDivision(divisionId: number): Observable<Port[]> {
    return this.http$.get<any[]>(`/api/container/parks/${divisionId}`)
      .pipe(
        map(results => {
          const obj = results
            .map(data => {
              data.display = data.name;
              if (data.code && data.code.length > 0) {
                data.display = `${data.display} (${data.code})`;
              }
              return data;
            });
          return obj;
        }),
        catchError(() => of([] as Port[]))
      ).pipe(take(1));
  }

  upsertContainerJobDefault(containerJobDefaultModel: ContainerJobDefault): Observable<HttpResponse<ContainerJobDefault>> {
    return this.http$.post<ContainerJobDefault>(`/api/container/default`, containerJobDefaultModel, { observe: 'response' }).pipe(take(1));
  }

  getDefaultFaf(freightPayerId: number, divisionId: number): Observable<number> {
    return this.http$.get<number>(`/api/containerjob/defaultFaf/${freightPayerId}/division/${divisionId}`).pipe(take(1));
  }

  getContainerJobDefaultByFreightPayer(freightPayerId: number, jobDefaultType: string): Observable<ContainerJobDefault | null> {
    const params = new HttpParams()
      .set('jobDefaultType', jobDefaultType)

    return this.http$.get<ContainerJobDefault | null>(`/api/formDefault/default/${freightPayerId}/`, { params }).pipe(take(1));
  }

  getJobTypes(): JobType[] {
    return [
      { id: JobTypeEnum.Import, name: "Import" },
      { id: JobTypeEnum.Export, name: "Export" },
      { id: JobTypeEnum.DomesticInbound, name: "Domestic Inbound" },
      { id: JobTypeEnum.DomesticOutbound, name: "Domestic Outbound" },
      // { id: JobTypeEnum.RailInbound, name: "Rail Inbound" },
      // { id: JobTypeEnum.RailOutbound, name: "Rail Outbound" },
      // { id: JobTypeEnum.IntermodalCoastal, name: "Intermodal Coastal" },
      // { id: JobTypeEnum.IntermodalRail, name: "Intermodal Rail" },
      // { id: JobTypeEnum.MoveMt, name: "Move MT" },
      // { id: JobTypeEnum.MoveFull, name: "Move FULL" },
      // { id: JobTypeEnum.OnSite, name: "On Site" },
    ];
  }

  getCanContainerBeSetBackToUnpack(containerLegId: number): Observable<boolean> {
    return this.http$.get<boolean>(`/api/container/${containerLegId}/canbesetbacktounpack`);
  }

  updateContainerJobItemStatus(model: ContainerUpdateStatusRequest[]) {
    return this.http$.put<void>(`/api/container/status`, model);
  }

  updateContainers(model: ContainerUpdateRequest): Observable<HttpResponse<void>> {
    return this.http$.put<void>(`/api/container/update`, model, { observe: 'response' })
      .pipe(take(1));
  }

  getContainerUpdateItems(containerJobItemIds: number[]): Observable<ContainerUpdateListItem[]> {
    let params = new HttpParams();

    containerJobItemIds.forEach(containerJobItemId => {
      params = params.append('containerJobItemIds', containerJobItemId.toString());
    });

    return this.http$.get<ContainerUpdateListItem[]>(`/api/container/update/items`, { params })
      .pipe(take(1));
  }

  completeContainerJob(model: ContainerCompleteLegRequest){
    return this.http$.post<void>(`/api/container/movement/complete`, model)
  }

  getContainerPortStatus(containerJobItemId: number): Observable<PortStatus> {
    return this.http$.get<PortStatus>(`/api/container/${containerJobItemId}/portstatus`, { headers: { 'skip-loading': 'true' } }).pipe(take(1));
  }

  getHazardCodes(): Observable<HazardCode[]> {
    return this.http$.get<HazardCode[]>(`/api/container/hazardCodes`).pipe(take(1));
  }
  getContainerSizes(): Observable<ContainerSize[]> {
    return this.http$.get<ContainerSize[]>(`/api/container/containerSizes`)
      .pipe(take(1));
  }

  getLoadUnloadTypes(): Observable<LoadUnloadType[]> {
    return this.http$.get<LoadUnloadType[]>(`/api/container/loadUnloadTypes`)
      .pipe(take(1));
  }

  getDoorDirections(): string[] {
    return ['Any', 'Front', 'Rear', 'B2B'];
  }

  getContainerTypes(): Observable<ContainerType[]> {
    return this.http$.get<any[]>(`/api/container/containerTypes`)
      .pipe(
        map(results => {
          const obj = results
            .map(data => {
              return {
                containerTypeId: data.containerTypeId,
                code: data.code,
                name: data.name,
                display: `${data.name}${data.code && data.code.length > 0
                  ? ` (${data.code})`
                  : ''}`
              }
            });
          return obj;
        })
      )
      .pipe(take(1));
  }

  getJobDivisions(): Observable<Division[]> {
    return this.http$.get<any[]>(`/api/container/regions/getAll`)
      .pipe(
        map(results => {
          const obj = results
            .map(data => {
              const division: Division = {
                regionId: data.regionId,
                name: data.name
              }
              return division;
            });
          return obj;
        })
      ).pipe(take(1));
  }
}
